import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

const faqs = [
  {
    question: 'Can I use BarsConnect if I only want a few shifts per week?',
    answer: 'Yes. Your profile can communicate your availability so bars can assess whether your schedule fits their needs.',
  },
  {
    question: 'What if my availability changes week to week?',
    answer: 'You can keep your profile details updated as your schedule evolves so your profile remains accurate.',
  },
  {
    question: 'Do I have to commit long-term if a bar contacts me?',
    answer: 'No. A message is the start of a conversation. You can review details and decide whether the opportunity fits.',
  },
  {
    question: 'Can I work with multiple bars through BarsConnect?',
    answer: 'Your profile can be viewed by different bars. You can evaluate each opportunity based on your own preferences and schedule.',
  },
  {
    question: 'Is there a minimum experience requirement to sign up?',
    answer: 'No minimum experience level is required to create a profile.',
  },
  {
    question: 'Is BarsConnect free?',
    answer: 'Yes. BarsConnect is free for bartenders during the beta period and no credit card is required for signup.',
  },
];

export default function PartTimeBartenderJobsPage() {
  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Part-Time Bartender Jobs — Flexible Shifts on BarsConnect',
      description: 'Find part-time bartender jobs that fit your schedule. Create a free BarsConnect profile and connect with bars looking for experienced bartenders for flexible shifts.',
      keywords: 'part-time bartender jobs, flexible bartending shifts, weekend bartender work, evening bar jobs',
      canonicalUrl: 'https://barsconnect.com/part-time-bartender-jobs',
      robots: 'index,follow',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'Part-Time Bartender Jobs', item: 'https://barsconnect.com/part-time-bartender-jobs' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="part-time-bartender-jobs-page">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10" data-testid="part-time-bartender-jobs-hero-section">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="part-time-bartender-jobs-title">Part-Time Bartender Jobs That Fit Your Schedule</h1>
          <p className="text-muted-foreground mb-4" data-testid="part-time-bartender-jobs-hero-copy-1">
            Part-time bartending is core hospitality work. BarsConnect helps you highlight your schedule and experience so bars can contact you for the shifts you actually want.
          </p>
          <p className="text-muted-foreground mb-6" data-testid="part-time-bartender-jobs-hero-copy-2">
            Create a free profile, set your availability, and connect directly with bar owners through the platform.
          </p>
          <Button asChild data-testid="part-time-bartender-jobs-primary-cta-btn">
            <Link to="/register/bartenders">Create Free Bartender Profile</Link>
          </Button>
        </header>

        <section className="mb-10" data-testid="part-time-bartender-jobs-how-it-works-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="part-time-bartender-jobs-how-it-works-heading">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-white/5 border border-white/10" data-testid="part-time-bartender-jobs-step-1-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="part-time-bartender-jobs-step-1-title">Step 1 — Set Up Your Profile</h3>
                <p className="text-sm text-muted-foreground" data-testid="part-time-bartender-jobs-step-1-copy">Describe your part-time goals, preferred shifts, and experience level.</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10" data-testid="part-time-bartender-jobs-step-2-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="part-time-bartender-jobs-step-2-title">Step 2 — Get Found by Bars</h3>
                <p className="text-sm text-muted-foreground" data-testid="part-time-bartender-jobs-step-2-copy">Bar owners can review your profile and reach out when your availability aligns with open shifts.</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10" data-testid="part-time-bartender-jobs-step-3-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="part-time-bartender-jobs-step-3-title">Step 3 — Connect on Your Terms</h3>
                <p className="text-sm text-muted-foreground" data-testid="part-time-bartender-jobs-step-3-copy">Review messages and choose opportunities that fit your priorities and schedule.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-10" data-testid="part-time-bartender-jobs-content-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="part-time-bartender-jobs-content-heading">Flexible Work Without Losing Professional Visibility</h2>
          <p className="text-muted-foreground mb-4" data-testid="part-time-bartender-jobs-content-copy-1">
            Whether you want weekend coverage, selected evening shifts, or a second income stream, your BarsConnect profile can communicate exactly what you are open to.
          </p>
          <p className="text-muted-foreground" data-testid="part-time-bartender-jobs-content-copy-2">
            You can also explore <Link className="text-primary hover:underline" to="/bartender-jobs" data-testid="part-time-bartender-jobs-inline-link-full-time">general bartender jobs</Link>,
            {' '}<Link className="text-primary hover:underline" to="/event-bartender-jobs" data-testid="part-time-bartender-jobs-inline-link-event">event bartender jobs</Link>, and
            {' '}<Link className="text-primary hover:underline" to="/bartender-jobs-near-me" data-testid="part-time-bartender-jobs-inline-link-near-me">local bartender opportunities</Link>.
          </p>
        </section>

        <section className="mb-10" data-testid="part-time-bartender-jobs-faq-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="part-time-bartender-jobs-faq-heading">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((item, index) => (
              <Card key={item.question} className="bg-white/5 border border-white/10" data-testid={`part-time-bartender-jobs-faq-card-${index + 1}`}>
                <CardContent className="p-5">
                  <h3 className="text-white font-semibold mb-2" data-testid={`part-time-bartender-jobs-faq-question-${index + 1}`}>{item.question}</h3>
                  <p className="text-sm text-muted-foreground" data-testid={`part-time-bartender-jobs-faq-answer-${index + 1}`}>{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section data-testid="part-time-bartender-jobs-repeat-cta-section">
          <Card className="bg-white/5 border border-white/10" data-testid="part-time-bartender-jobs-repeat-cta-card">
            <CardContent className="p-6 sm:p-8">
              <h2 className="text-2xl font-semibold text-white mb-3" data-testid="part-time-bartender-jobs-repeat-cta-heading">Build a profile that matches your schedule.</h2>
              <p className="text-muted-foreground mb-5" data-testid="part-time-bartender-jobs-repeat-cta-copy">Your profile can stay active so bars can contact you whenever relevant part-time shifts open.</p>
              <div className="flex flex-wrap gap-3">
                <Button asChild data-testid="part-time-bartender-jobs-repeat-cta-register-btn">
                  <Link to="/register/bartenders">Create Free Bartender Profile</Link>
                </Button>
                <Button asChild variant="outline" data-testid="part-time-bartender-jobs-repeat-cta-app-btn">
                  <Link to="/bartender-app">See the BarsConnect Bartender App Experience</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
